import type { HCP_REGISTRIES } from "../../../../../api/src/shared/domain/hcp-registry";
import { REDIRECT_MEASUREMENT_ID } from "../../../config";
import { gaPushEvent } from "../base.analytics";
import type { PushOptInOutcome } from "../../../domains/notification/hooks/useNotificationOptIn";
import type { PracticePreSelection } from "../../../domains/practice/practiceProfileSelection";
import type { AcquisitionChannelValue } from "../../../domains/user/utils/acquisitionChannels";

enum ProtocolEvent {
  CAMPAIGN_DETAILS = "campaign_details",
}

enum CustomEvent {
  SHORT_URL_FOLLOWED = "short_url_followed",
  SHORT_URL_CONVERTED = "short_url_converted",
}

enum OnboardingEvent {
  NAMES_SUBMITTED = "names_submitted",
  COUNTRY_SUBMITTED = "country_submitted",
  REGISTRY_LOOKUP_RESULT = "hcp_registry_lookup_result",
  REGISTRY_PROFILE_CONFIRMED = "hcp_registry_profile_confirmed",
  REGISTRY_STEP_RESOLVED_MANUALLY = "hcp_registry_step_resolved_manually",
  PROFESSION_SUBMITTED = "profession_submitted",
  MAIN_SPECIALTY_SUBMITTED = "main_specialty_submitted",
  MATCHING_PUBLICATIONS_SUBMITTED = "matching_publications_submitted",
  MATCHING_PUBLICATIONS_SKIPPED = "matching_publications_skipped",
  RELEVANT_TOPICS_SUBMITTED = "relevant_topics_submitted",
  ACQUISITION_CHANNEL_SUBMITTED = "acquisition_channel_submitted",
  FEED_TASTE_EXPLORED = "feed_taste_explored",
  PREFERENCE_ONBOARDING_NUDGE_VIEWED = "preference_onboarding_nudge_viewed",
  PREFERENCE_ONBOARDING_OPENED = "preference_onboarding_opened",
  PREFERENCE_ONBOARDING_SNOOZED = "preference_onboarding_snoozed",
  PREFERENCE_ONBOARDING_COMPLETED = "preference_onboarding_completed",
  PUSH_NOTIFICATIONS_DONE = "push_notifications_done",
  PUSH_OPT_IN_SHOWN = "push_opt_in_shown",
  PUSH_OPT_IN_ANSWERED = "push_opt_in_answered",
  SIGNUP_SHEET_OPENED = "signup_sheet_opened",
  SIGNUP_SHEET_DISMISSED = "signup_sheet_dismissed",
  AUTH_METHOD_SELECTED = "auth_method_selected",
  AUTH_FAILED = "auth_failed",
  OTP_REQUESTED = "otp_requested",
  OTP_VERIFIED = "otp_verified",
  OTP_FAILED = "otp_failed",
  SIGNUP_STEP_VIEWED = "signup_step_viewed",
  SIGNUP_STEP_BACK = "signup_step_back",
  HCP_GATE_ANSWERED = "hcp_gate_answered",
  PRACTICE_QUESTION_SHOWN = "practice_question_shown",
  PRACTICE_RECAP_EDIT_CLICKED = "practice_recap_edit_clicked",
  PRACTICE_PROFILES_CONFIRMED = "practice_profiles_confirmed",
  PRACTICE_QUESTION_SKIPPED = "practice_question_skipped",
  PRACTICE_OFFER_ABANDONED = "practice_offer_abandoned",
  PRACTICE_SEARCH_NO_RESULT = "practice_search_no_result",
  PRACTICE_PROFILING_GATE_SHOWN = "practice_profiling_gate_shown",
  PRACTICE_PROFILING_GATE_DISMISSED = "practice_profiling_gate_dismissed",
  PUB_SEARCH_NO_RESULTS = "pub_search_no_results",
  PUB_SEARCH_ERROR = "pub_search_error",
}

export type StepSkipTrigger = "publications" | "topics";

/** Where the practitioner stood when the offer was given up on. Two different
 * outcomes, not two values of one measure — hence not a boolean. */
export type PracticeOfferAbandonPoint = "before_slide" | "on_slide";

/** What ended the wait. A catalogue too slow to answer and a request that came
 * back with no answer are two different repairs — one endpoint, one outage — and
 * folded into a single value they read as the same "no catalogue" this event
 * exists to break apart. `deadline` means the wait RAN OUT, which also covers a
 * request cancelled and never replaced: the query wrapper reads a cancellation
 * as still pending, so the clock is what ends it. */
export type PracticeOfferAbandonCause = "deadline" | "request_failed";

export type SignupSurface =
  | "bottom_sheet"
  /** The onboarding carousel at `/signup/onboarding`. It kept the name it had
   * when a `/signup` page stood in front of it; that page is gone (the sheet
   * replaced it) and the carousel is the only emitter left. */
  | "signup_page"
  | "login_page"
  /** The desktop auth modal over JuisciGPT, which is that platform's one door. */
  | "desktop_gate"
  /** The same modal, opened from the sign-up offer that follows a logged-out
   * visitor's JuisciGPT answer (`GPTAnonymousChatPage`). Its own value so the
   * anonymous-answer → signup funnel splits from the front door. */
  | "anonymous_gpt_wall"
  /** The same modal, shown at once by the logged-out JuisciGPT page on a
   * browser an account has signed in on: its owner logs in rather than asking
   * anonymously. */
  | "anonymous_gpt_member";
/**
 * The steps signup can emit — exactly the carousel slides, nothing else.
 *
 * `pub_search`, `interests`, `conversation`, `room_suggestions`, `notifications`
 * and `room_preview` used to live here after their screens left signup. They
 * outlived their emitter by months and were read as live instrumentation: the
 * push step's `step=notifications` reaching 0 was investigated as a tracking
 * regression when the step simply no longer existed. A name nobody can emit
 * belongs in neither the union nor a report — the ones still arriving in
 * Mixpanel come from stale client bundles, which is what `signup_flow` below
 * exists to make visible.
 */
export type SignupStepName =
  | "intro"
  | "country"
  | "name"
  | "otp"
  | "registry"
  | "profession"
  | "specialty"
  | "practice"
  | "partner_objection";
export type AuthMethod = "apple" | "google" | "email" | "qr_code";
export type AuthIntent = "signup" | "login";
export type ProfilingFlow = "signup" | "preference_refresh";

const sessionIdStore = (() => {
  let session: {
    id: string | null;
    expiration: number;
  } = {
    id: null,
    expiration: 0,
  };

  const getSessionId = () => {
    if (session.expiration < Date.now()) {
      session = {
        id: null,
        expiration: 0,
      };
    }
    return session.id;
  };

  const setSessionId = (id: string, expiration: number = 1000 * 3600) => {
    session = {
      id,
      expiration: Date.now() + expiration,
    };
  };

  return {
    getSessionId,
    setSessionId,
  };
})();

export function gaSetCampaignDetails() {
  const params = Object.fromEntries(
    new URLSearchParams(globalThis.location.search)
  );
  const utmSource = params.utm_source;
  if (!utmSource) {
    return;
  }

  if (utmSource !== "juisci_short_url") {
    // Bail if the source is not the Juisci short URL service.
    return;
  }

  if (params.session_id) {
    sessionIdStore.setSessionId(params.session_id);
  }

  // `campaign_details` is a protocol event used to send campaign details that will be applied
  // to all future events.
  // In BigQuery, this data should be available in the future event's `traffic_source` column.
  // See https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference/events#campaign_details
  gaPushEvent(ProtocolEvent.CAMPAIGN_DETAILS, {
    ...Object.fromEntries(
      Object.entries(params)
        .filter(([key]) => key.startsWith("utm_"))
        .map(([key, value]) => [key.replace("utm_", ""), value])
    ),
    // Warning: Setting session_id will force the creation of a new session!
    ...(params.session_id && { session_id: params.session_id }),
    engagement_time_msec: 1000,
  });
}

export function gaEventShortUrlFollowed() {
  if (!sessionIdStore.getSessionId()) {
    return;
  }

  // This is sent to the server-side data stream.
  gaPushEvent(CustomEvent.SHORT_URL_FOLLOWED, {
    send_to: REDIRECT_MEASUREMENT_ID,
    session_id: sessionIdStore.getSessionId(),
  });
}

// TODO: this could be automated, by having a `&converted_on=/destination_path` in
// the params and checking if the destination path is reached within the session.
export function gaEventShortUrlConverted() {
  if (!sessionIdStore.getSessionId()) {
    return;
  }

  // This is sent to the server-side data stream.
  gaPushEvent(CustomEvent.SHORT_URL_CONVERTED, {
    send_to: REDIRECT_MEASUREMENT_ID,
    session_id: sessionIdStore.getSessionId(),
  });
}

export function gaEventNamesSubmitted() {
  gaPushEvent(OnboardingEvent.NAMES_SUBMITTED);
}

export function gaEventCountrySubmitted() {
  gaPushEvent(OnboardingEvent.COUNTRY_SUBMITTED);
}

// --- RPPS (France-only onboarding step) ---

/** How the user reached/resolved an RPPS record. */
export type RegistryLookupMode = "name" | "identifier";
/** Outcome of an RPPS directory lookup. */
export type RegistryLookupOutcome =
  | "found"
  | "multiple"
  | "too_many_results"
  | "not_found"
  | "unavailable"
  | "inactive";
/** How the user left the RPPS step without a confirmed match. */
export type RegistryManualResolution =
  "rejected" | "skipped" | "id_stored" | "out_of_scope" | "manual";

/**
 * Result of an RPPS directory lookup — fired once per resolved lookup, for both
 * the automatic name search (`mode: "name"`) and the manual registry-id search
 * on the not-found fallback (`mode: "identifier"`). `candidate_count` is 1 for
 * found, the list length for multiple, 0 for not_found.
 */
export function gaEventRegistryLookupResult(params: {
  registry: keyof typeof HCP_REGISTRIES | "reps";
  mode: RegistryLookupMode;
  outcome: RegistryLookupOutcome;
  candidate_count: number;
}) {
  gaPushEvent(OnboardingEvent.REGISTRY_LOOKUP_RESULT, params);
}

/**
 * User confirmed an RPPS profile ("Yes, this is me"). `fast_tracked` is true
 * when the matched profession + specialty mapped to app entries, so the
 * profession/specialty steps were skipped and onboarding finished directly.
 */
export function gaEventRegistryProfileConfirmed(params: {
  registry: keyof typeof HCP_REGISTRIES | "reps";
  match_mode: RegistryLookupMode;
  from_multiple: boolean;
  fast_tracked: boolean;
}) {
  gaPushEvent(OnboardingEvent.REGISTRY_PROFILE_CONFIRMED, params);
}

/** Records why the user left the registry step without a confirmed profile. */
export function gaEventRegistryStepResolvedManually(params: {
  registry: keyof typeof HCP_REGISTRIES | "reps";
  via: RegistryManualResolution;
}) {
  gaPushEvent(OnboardingEvent.REGISTRY_STEP_RESOLVED_MANUALLY, params);
}

export function gaEventProfessionSubmitted() {
  gaPushEvent(OnboardingEvent.PROFESSION_SUBMITTED);
}

export function gaEventMainSpecialtySubmitted() {
  gaPushEvent(OnboardingEvent.MAIN_SPECIALTY_SUBMITTED);
}

export function gaEventPushNotificationsDone() {
  gaPushEvent(OnboardingEvent.PUSH_NOTIFICATIONS_DONE);
}

/**
 * Where a push opt-in ask was rendered. The push step left signup in
 * `65f3890bd8`, so the opt-in now lives on these surfaces — and each one has to
 * name itself, or the pair of events below collapses into a single number that
 * cannot tell a good moment to ask from a bad one.
 */
export type PushOptInSurface =
  | "profiling_completed"
  | "explore_entry"
  | "gpt_chat_card"
  | "notification_fixup";

/** The ask reached the screen — the denominator of the opt-in rate. */
export function gaEventPushOptInShown(params: { surface: PushOptInSurface }) {
  gaPushEvent(OnboardingEvent.PUSH_OPT_IN_SHOWN, params);
}

/** What the user answered — the numerator. Fires on every exit of an ask. */
export function gaEventPushOptInAnswered(params: {
  surface: PushOptInSurface;
  outcome: PushOptInOutcome;
}) {
  gaPushEvent(OnboardingEvent.PUSH_OPT_IN_ANSWERED, params);
}

/**
 * "How did you hear about us?", answered in the JuisciGPT card (desktop corner,
 * phone composer band). `channel` is one of the seven presets: the cards offer
 * no "other", so the event carries no free text.
 */
export function gaEventAcquisitionChannelSubmitted(params: {
  channel: AcquisitionChannelValue;
}) {
  gaPushEvent(OnboardingEvent.ACQUISITION_CHANNEL_SUBMITTED, params);
}

export function gaEventFeedTasteExplored(params: { flow: ProfilingFlow }) {
  gaPushEvent(OnboardingEvent.FEED_TASTE_EXPLORED, params);
}

type PreferenceOnboardingAnalyticsParams = {
  policy_version: "v1";
  surface: "discovery";
  eligibility_reason: "profile_missing" | "low_confidence";
};

export function gaEventPreferenceOnboardingNudgeViewed(
  params: PreferenceOnboardingAnalyticsParams
) {
  gaPushEvent(OnboardingEvent.PREFERENCE_ONBOARDING_NUDGE_VIEWED, params);
}

export function gaEventPreferenceOnboardingOpened(
  params: PreferenceOnboardingAnalyticsParams
) {
  gaPushEvent(OnboardingEvent.PREFERENCE_ONBOARDING_OPENED, params);
}

export function gaEventPreferenceOnboardingSnoozed(
  params: PreferenceOnboardingAnalyticsParams
) {
  gaPushEvent(OnboardingEvent.PREFERENCE_ONBOARDING_SNOOZED, params);
}

export function gaEventPreferenceOnboardingCompleted(
  params: PreferenceOnboardingAnalyticsParams
) {
  gaPushEvent(OnboardingEvent.PREFERENCE_ONBOARDING_COMPLETED, params);
}

export function gaEventSignupSheetOpened(params: {
  surface: SignupSurface;
  is_invited_to_room: boolean;
}) {
  gaPushEvent(OnboardingEvent.SIGNUP_SHEET_OPENED, params);
}

export function gaEventSignupSheetDismissed(params: {
  had_typed_email: boolean;
}) {
  gaPushEvent(OnboardingEvent.SIGNUP_SHEET_DISMISSED, params);
}

export function gaEventAuthMethodSelected(params: {
  method: AuthMethod;
  intent: AuthIntent;
  surface: SignupSurface;
}) {
  gaPushEvent(OnboardingEvent.AUTH_METHOD_SELECTED, params);
}

/**
 * Analytics taxonomy for auth failures. Deliberately NOT the raw Firebase code:
 * the SDK's list is open-ended and its strings are a wire format we don't own.
 * Unmapped codes land on "unknown" — when that bucket grows, add the code to
 * `resolveAuthErrorCode` rather than reading raw codes in reports.
 */
export type AuthErrorCode =
  | "wrong_password"
  | "invalid_credential"
  | "user_not_found"
  | "invalid_email"
  | "email_already_in_use"
  | "provider_collision"
  | "rate_limited"
  | "network"
  | "popup_closed"
  | "user_disabled"
  | "internal_error"
  | "unknown";

/** Boundary conversion: Firebase hands us an arbitrary `error.code` string. */
export function resolveAuthErrorCode(firebaseCode: string): AuthErrorCode {
  switch (firebaseCode) {
    case "auth/wrong-password":
      return "wrong_password";
    case "auth/invalid-credential":
      return "invalid_credential";
    case "auth/user-not-found":
      return "user_not_found";
    case "auth/invalid-email":
      return "invalid_email";
    case "auth/email-already-in-use":
      return "email_already_in_use";
    case "auth/account-exists-with-different-credential":
      return "provider_collision";
    case "auth/too-many-requests":
      return "rate_limited";
    case "auth/network-request-failed":
      return "network";
    case "auth/popup-closed-by-user":
      return "popup_closed";
    case "auth/user-disabled":
      return "user_disabled";
    case "auth/internal-error":
      return "internal_error";
    default:
      return "unknown";
  }
}

/**
 * Why a code did not open the door.
 *
 * `rate_limited` is the odd one out and belongs here anyway: from the user's
 * side "you cannot have another code yet" is the same dead end as a wrong one,
 * and separating it into its own event would leave the four states of the
 * screen split across two reports.
 */
export type OtpFailureReason =
  "wrong_code" | "expired" | "too_many_attempts" | "rate_limited";

/**
 * A code was asked for. The denominator of everything else about the OTP: a
 * request with no `otp_verified` behind it is a login lost in the mail, which
 * is the one failure this flow produces silently (PRD §5 guardrail).
 */
export function gaEventOtpRequested(params: {
  intent: AuthIntent;
  surface: SignupSurface;
  /** A first send and a "Resend code" are not the same event to read: a high
   * resend share is a deliverability problem, not a user problem. */
  is_resend: boolean;
}) {
  gaPushEvent(OnboardingEvent.OTP_REQUESTED, params);
}

export function gaEventOtpVerified(params: {
  intent: AuthIntent;
  /** Entries it took, the successful one included. */
  attempts: number;
  /** Request to acceptance. It measures the MAILBOX, not the user: a p90 that
   * climbs is a hospital spam filter holding the code past its TTL. */
  latency_ms: number;
}) {
  gaPushEvent(OnboardingEvent.OTP_VERIFIED, params);
}

export function gaEventOtpFailed(params: {
  reason: OtpFailureReason;
  intent: AuthIntent;
}) {
  gaPushEvent(OnboardingEvent.OTP_FAILED, params);
}

export function gaEventAuthFailed(params: {
  code: AuthErrorCode;
  method: AuthMethod;
  intent: AuthIntent;
  surface: SignupSurface;
  /**
   * The provider this address is ALREADY registered with. Only resolvable on a
   * provider collision — null everywhere else. This is the property that answers
   * "how many people signed up with Apple/Google and then try email+password".
   */
  existing_provider: "apple" | "google" | "email" | null;
}) {
  gaPushEvent(OnboardingEvent.AUTH_FAILED, params);
}

/**
 * The ordered step list the emitting build ships — the signup flow's identity.
 *
 * Passed as the typed list and serialized here, at the wire boundary, so the
 * value cannot drift from the flow: it IS the flow's step order, not a version
 * number someone has to remember to bump. Two builds with different steps
 * produce different values, which is the whole point — a completion rate
 * computed across a flow change silently mixes two products (58 % over 90 days
 * vs 53 % over 30 was exactly this), and stale client bundles keep emitting the
 * old shape for weeks after a deploy.
 *
 * Deliberately NOT named `flow`: that property already exists on
 * `relevant_topics_submitted` / `feed_taste_explored` with an unrelated value
 * space ("signup" | "preference_refresh"), and one property name holding two
 * meanings is unreadable in the Lexicon.
 */
function serializeSignupFlow(flow: readonly SignupStepName[]): string {
  return flow.join(">");
}

export function gaEventSignupStepViewed(params: {
  step: SignupStepName;
  step_index: number;
  signup_flow: readonly SignupStepName[];
  is_invited_to_room: boolean;
}) {
  gaPushEvent(OnboardingEvent.SIGNUP_STEP_VIEWED, {
    ...params,
    signup_flow: serializeSignupFlow(params.signup_flow),
  });
}

export function gaEventSignupStepBack(params: {
  step: SignupStepName;
  signup_flow: readonly SignupStepName[];
  is_invited_to_room: boolean;
}) {
  gaPushEvent(OnboardingEvent.SIGNUP_STEP_BACK, {
    ...params,
    signup_flow: serializeSignupFlow(params.signup_flow),
  });
}

/**
 * The healthcare-professional gate on the intro slide, answered.
 *
 * ONE event carrying both answers, never an event on the decline alone: the
 * decline is only readable against the number of people who were asked, and a
 * decline-only event forces that denominator to be reconstructed from a
 * different event on every chart.
 *
 * "Declined" is not an exit — both CTAs advance to the name step. It is a
 * declaration, and it is the only qualified signal we get that someone is
 * outside the target audience; without it their later abandon is
 * indistinguishable from a churned clinician.
 */
export function gaEventHcpGateAnswered(params: {
  outcome: "confirmed" | "declined";
  is_invited_to_room: boolean;
}) {
  gaPushEvent(OnboardingEvent.HCP_GATE_ANSWERED, params);
}

/**
 * Where a ticked row came from, for analytics ONLY.
 *
 * Deliberately NOT `PracticePreSelection["source"]`, which is the API's own
 * union and stays binary: `agent | none` is served on the wire, read back by
 * this webapp, and stored in `practice_inferences.source` under a database
 * CHECK. Widening it would have to move all three at once, to record a
 * distinction none of them makes.
 *
 * `campaign` is that distinction: the SAME inference, run hours earlier off a
 * persisted row instead of live while the practitioner waited.
 *
 * ⚠️ It says the SCREEN WAS SEEDED FROM A PERSISTED ROW — not that the reader
 * came from the email. `/onboarding/practice` is also the Explore gate
 * (`AppRoutes`), so a campaigned account that never opened the invite reaches
 * the same seeded picker and reports the same bucket. Separating arrival from
 * seeding needs the UTMs on the session, which the CTA does carry
 * (`withEmailCampaign`); this field alone cannot do it.
 */
type PreSelectionSource =
  | PracticePreSelection["source"]
  | "campaign"
  /**
   * The reading the agent made at signup, kept when the practitioner pressed
   * "Later", and seeding the screen the app asks again on. Not `campaign`: no
   * campaign ran, and the population is the one that put the question off.
   */
  | "signup_deferred"
  /**
   * The practitioner's OWN saved answer checked the screen — the settings editor
   * reopened on what they said last time.
   *
   * Not `none`, which is what this reported before and which is a different
   * claim: `none` says nothing checked the screen, and pairing it with a
   * non-zero baseline made the event contradict itself. It is not `agent` or
   * `campaign` either, because no inference ran: a practitioner correcting
   * themselves in settings is not evidence about the agent, and averaged into
   * the agent's accuracy it would be read as one.
   */
  | "saved_answer";

/**
 * Which of the practice question's two screens the practitioner met.
 *
 * `recap` is the agent's answer read back as names; `picker` is the catalogue.
 * Arriving straight on the picker is not a failure of the recap, it is the agent
 * having recognised nothing — which is why this travels next to `recognised_practice_count`
 * and never alone.
 */
export type PracticeQuestionScreen = "recap" | "picker";

/**
 * The practice question reached a practitioner, with what the agent found.
 *
 * The DENOMINATOR of everything the practice screens are judged on, and the only
 * place the agent's yield is reported. `practice_profiles_confirmed` fires at the
 * end of signup, after the acquisition slide and only once the write lands, so it
 * counts nobody who abandoned in between — a confirmation rate computed against
 * it divides by its own numerator's survivors. This event fires when the screen
 * appears, so the population it counts is the one that was actually asked.
 *
 * `recognised_practice_count` is what the agent recognised, and it is NOT `catalogue_size`:
 * that one is how many practices the Specialty contains, which says nothing about
 * this practitioner. The confirmed event carried only the second under a name that
 * read like the first (`total_offered_profiles`), and the agent's yield — the mean,
 * the distribution, the share recognised at all — could not be read anywhere.
 *
 * Both flows emit it, and `flow` is what keeps them apart: signup meets the
 * question inside the carousel, an existing account meets it at the Explore gate,
 * and the two populations have no reason to answer alike. Counted once per session
 * per Specialty, like every other impression (`useTrackOnMount`) — Back returns to
 * the recap, and a denominator that counts remounts produces rates above 1.
 */
export function gaEventPracticeQuestionShown(params: {
  flow: ProfilingFlow;
  screen: PracticeQuestionScreen;
  specialty_uid: string;
  /**
   * Practices the AGENT recognised for this practitioner. Zero is the value that
   * says the agent found nothing, and it is a first-class answer, not a gap.
   *
   * Never the practitioner's own saved answer, which is what the settings editor
   * opens on and what this counted before: a reader correcting themselves would
   * have entered the agent's accuracy as though the agent had proposed their
   * choices. `pre_selection_source` says which of the two checked the screen, and
   * this field stays zero unless it reads `agent` or `campaign`.
   *
   * ⚠️ NOT named `inferred_count`, however much it reads like one. That name is
   * TAKEN, by `practice_invite_sent` / `practice_invite_skipped`, where the
   * workflow tracking layer types every property as a string
   * (`workflow/actions/track-event.ts`: `z.record(z.string(), z.string())`), so
   * the campaign sends `String(result.inferredCount)`. One name carrying a string
   * on those events and a number here gives the Mixpanel Lexicon one type for two
   * meanings, and the type that survives is the one with months of history —
   * which would leave the mean and the distribution of this field, the two
   * numbers it exists for, unaggregatable. The server cannot send a number
   * without changing that layer for every workflow event; this field had no
   * history at all when the clash was found, so this is the side that moved.
   */
  recognised_practice_count: number;
  /** Practices the Specialty's catalogue holds. The ceiling `recognised_practice_count` is
   * read against, and what makes a thin catalogue distinguishable from a silent
   * agent. */
  catalogue_size: number;
  pre_selection_source: PreSelectionSource;
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_QUESTION_SHOWN, params);
}

/**
 * "Edit my practices" on the recap, pressed.
 *
 * The intent to correct the agent, which nothing else reports. `confirmed_from`
 * on the confirmed event names the screen a practitioner LEFT from, so it counts
 * only those who pressed this AND finished signup — and it spells `picker` for
 * someone who never had a recap in the first place. Neither of those is the
 * number "how many people the agent did not convince".
 *
 * `opens` is which screen the press actually reveals. An agent that named
 * practices without naming a side still owes its axis question, so Edit lands on
 * `axis` rather than the catalogue, and a drop-off there is a drop-off on a
 * question, not on a list.
 */
export function gaEventPracticeRecapEditClicked(params: {
  specialty_uid: string;
  /** The recap's own size, so the press is readable against what it rejected:
   * editing one recognised practice and editing six are different gestures. */
  recognised_practice_count: number;
  opens: "axis" | "profiles";
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_RECAP_EDIT_CLICKED, params);
}

/**
 * "Later" on the signup practice slide: the question is put off, and the app
 * asks it again before Explore.
 *
 * Once per session per Specialty, like `practice_question_shown`. Only the
 * `recap`, `axis` and `picker` skips divide by that event into a skip rate: the
 * wait fires no impression, and its catalogue can still turn out empty.
 * `skipped_from` names the screen it was pressed on: a skip during the wait
 * says the agent was too slow, one on the recap says it did not convince.
 * `nothing_recognised` is no press: the agent checked nothing (no practice
 * found, a failure or the deadline), and the slide left on its own.
 */
export function gaEventPracticeQuestionSkipped(params: {
  flow: "signup";
  specialty_uid: string;
  skipped_from:
    "inferring" | "recap" | "axis" | "picker" | "nothing_recognised";
  recognised_practice_count: number;
  /** Null when "Later" was pressed before the catalogue arrived. */
  catalogue_size: number | null;
  pre_selection_source: PreSelectionSource;
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_QUESTION_SKIPPED, params);
}

/**
 * The Practice Profiles screen's answer (practice-profiles ticket 08).
 *
 * The measurement this exists for is the CONFIRMATION rate, against the 1.2% of
 * the existing base who ever completed the equivalent interest-declaration
 * screen. So the two numbers that matter are how many were offered and how many
 * were kept — and `changed_selection`, which separates a practitioner who
 * actively confirmed from one who simply walked past the screen the agent had
 * checked. Those are different products, and a single "completed" boolean would
 * report them as the same one.
 */
export function gaEventPracticeProfilesConfirmed(params: {
  flow: ProfilingFlow;
  /**
   * How many practices the Specialty's catalogue holds.
   *
   * Named `total_offered_profiles` until the practice question got its own
   * impression event, and the old name is why: it reads as "what the agent
   * offered this practitioner", it has always been the size of the CATALOGUE,
   * and every delta computed against it was wrong by the whole catalogue. What
   * the agent found is `recognised_practice_count`, below.
   */
  catalogue_size: number;
  total_confirmed_profiles: number;
  /**
   * What the agent recognised, and the baseline the three deltas below are
   * counted from. The same number `practice_question_shown` carries, repeated
   * here so the answer is readable without a join back to the impression.
   */
  recognised_practice_count: number;
  /**
   * What the practitioner changed about the answer THE SCREEN OPENED WITH, which
   * `pre_selection_source` names: the agent's reading at signup, the campaign's
   * on the Explore gate, their own saved answer in settings. Only the first two
   * are a correction of the agent — read these against `agent`/`campaign` when
   * that is the question, or the settings edits are averaged in.
   *
   * Gross, both of them: someone who unticks two and adds three has added three
   * and removed two, and the net alone (+1) reports that as a light correction.
   * The net is `added_count - removed_count` and is derivable; these two are not
   * derivable from the net.
   */
  added_count: number;
  removed_count: number;
  /**
   * Of `added_count`, how many were ticked with the search field in use, and of
   * those, how many sat on a row only the assisted search surfaced.
   *
   * The field is the answer for a practitioner the catalogue describes under a
   * word they did not think of, and it is the half of "I said it myself" that
   * leaves a practice uid behind — the other half is `declared`, where they kept
   * their own words instead. Without the split, a practice reached by typing is
   * indistinguishable from one found by scrolling, and the assisted layer cannot
   * be told from the string layer it sits behind.
   */
  added_via_search_count: number;
  added_via_assist_count: number;
  /**
   * Whether a recap was ever on screen for this answer.
   *
   * `confirmed_from` cannot say it: `picker` is both "opened the catalogue from
   * the recap" and "never had a recap because the agent found nothing", and
   * those two populations answer the question for opposite reasons.
   */
  had_recap: boolean;
  changed_selection: boolean;
  answered_disambiguation: boolean;
  /**
   * How many sides of the axis were named. The question takes several now, so
   * `answered_disambiguation` alone can no longer tell a practitioner with one
   * species from one with four — and whether multi-cohort practice is common or
   * a long tail is exactly the claim this change rests on. Zero when the
   * Specialty asks nothing.
   */
  disambiguation_count: number;
  // Which source checked the screen: the agent live at signup, the persisted
  // campaign reading, or nothing — in which case the practitioner answered an
  // empty list. Reported because the confirmation rate this screen exists to
  // move is only interpretable per source, and an aggregate cannot test the
  // claim.
  pre_selection_source: PreSelectionSource;
  /**
   * Which screen the practitioner pressed Continue on.
   *
   * The whole claim of the recap is that a short answer read back beats a long
   * one arbitrated, so this is the field that tests it: `recap` means the agent's
   * answer was accepted as it stood, `picker` means the catalogue was opened.
   * `changed_selection` cannot say it — someone who opens the list, reads it and
   * changes nothing reports `false` there and `picker` here, and that difference
   * is what says whether the list was worth opening.
   *
   * `declaration` is the free-text exit: they left by keeping their own words,
   * which is the population this whole arm exists to serve, so it is the number
   * that says whether the catalogue has a hole worth filling.
   *
   * Always `picker` outside signup: settings and the Explore gate have neither a
   * recap nor a declaration.
   */
  confirmed_from: "recap" | "picker" | "declaration";
  /**
   * Whether the answer carries declared words at all. Not the same question as
   * the field above, which names the last gesture: someone can declare, walk
   * back, check a row and leave on Continue.
   */
  declared: boolean;
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_PROFILES_CONFIRMED, params);
}

/**
 * The Practice Profiles offer was given up on before it answered
 * (practice-profiles ticket 09).
 *
 * `before_slide` is the ceiling doing its job — nobody saw a screen.
 * `on_slide` means a practitioner watched a wait and got nothing, which is a
 * defect. Both otherwise read as "had no catalogue", which is how the second one
 * shipped unnoticed once.
 *
 * Fired from the offer's VERDICT, not from a user action, and not from the clock
 * alone: a deadline that runs out and a request that fails leave the practitioner
 * in the same place, so both emit, and `reason` keeps them apart. Wired to the
 * clock alone, this event counted zero for a 100 ms endpoint (ticket 15).
 */
export function gaEventPracticeOfferAbandoned(params: {
  abandoned_at: PracticeOfferAbandonPoint;
  reason: PracticeOfferAbandonCause;
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_OFFER_ABANDONED, params);
}

/**
 * A practitioner searched the practices of their Specialty and the catalogue had
 * nothing (practice-profiles ticket 10).
 *
 * This is the counterweight to making one practice mandatory. The rule rests on
 * the premise that an expert-reviewed catalogue covers its Specialty, so
 * "nothing describes me" reports a hole in the catalogue. Blocking hides exactly
 * that: someone who cannot find their practice ticks the least wrong row, the
 * confirmation rate reads 100%, and the hole is invisible in every number the
 * feature is judged on. This event is the only place it shows up.
 *
 * NOT fed into the research Gaps (`gap.ts`), which derive from
 * `search_query_logs` and are a claim about the CORPUS. This is a claim about
 * the CATALOGUE, and averaging the two would make each unreadable.
 *
 * Searched over the WHOLE offer, not the rows the axis answer left on screen: a
 * practice hidden behind an unanswered side is reachable in one tap, and
 * reporting it as absent would blame the catalogue for the narrowing working.
 *
 * Fires per keystroke past three characters, so a genuinely absent practice
 * arrives as a handful of events rather than one. That is the same shape as the
 * corpus search logs, and deduplicating it client-side would need a state
 * machine in a presentational component.
 */
export function gaEventPracticeSearchNoResult(params: {
  flow: ProfilingFlow;
  query: string;
  specialty_uid: string;
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_SEARCH_NO_RESULT, params);
}

/**
 * The practice gate held an already-signed-up practitioner in front of Explore
 * (practice-profiles ticket 11).
 *
 * The counterpart of `PRACTICE_PROFILES_CONFIRMED`, which only the practitioners
 * who answered ever emit: without this one, abandoning the gate is
 * indistinguishable from never having been shown it, and the confirmation rate
 * has no denominator.
 *
 * Once per session, from the page rather than the redirect: the redirect
 * re-renders, the page is what a practitioner actually met.
 */
export function gaEventPracticeProfilingGateShown(params: {
  specialty_uid: string;
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_PROFILING_GATE_SHOWN, params);
}

/**
 * The practitioner closed the practice gate instead of answering it
 * (practice-profiles ticket 18).
 *
 * The numerator `PRACTICE_PROFILING_GATE_SHOWN` has never had. Without it, the
 * practitioners who met the question and refused it are indistinguishable from
 * the ones who met it and simply have not come back yet — one number for two
 * populations, which is the same defect `PRACTICE_OFFER_ABANDONED` was written
 * to remove one screen earlier.
 *
 * Once per session, like the shown event it divides into: the page re-presents
 * itself on every Explore request, so counting mounts would make the rate
 * exceed 1. It therefore means "closed the question at least once this session",
 * never "refused for good" — whether they came back is what
 * `PRACTICE_PROFILES_CONFIRMED` says.
 *
 * Carries no "which way out" prop, deliberately. The page's only handle on that
 * is `history.length`, which on the web counts the whole TAB — referrer
 * included — so a practitioner who arrived from an external link and met the
 * question cold is indistinguishable from one who came looking for Explore. A
 * measure that is right only in the native webview is worse than no measure.
 */
export function gaEventPracticeProfilingGateDismissed(params: {
  specialty_uid: string;
}) {
  gaPushEvent(OnboardingEvent.PRACTICE_PROFILING_GATE_DISMISSED, params);
}

export function gaEventMatchingPublicationsSubmitted(params: {
  total_found_publications: number;
  total_confirmed_publications: number;
}) {
  gaPushEvent(OnboardingEvent.MATCHING_PUBLICATIONS_SUBMITTED, params);
}

export function gaEventMatchingPublicationsSkipped(params: {
  step_skip_triggered: StepSkipTrigger;
}) {
  gaPushEvent(OnboardingEvent.MATCHING_PUBLICATIONS_SKIPPED, params);
}

export function gaEventRelevantTopicsSubmitted(params: {
  flow: ProfilingFlow;
  total_found_topics: number;
  total_confirmed_topics: number;
  confirmed_topics: string[];
  // Of the options shown, how many came from the conversation-learned UPP layer
  // (`learnedTopics`), and how many of those the user actually kept. Without the
  // split, a confirmed topic is indistinguishable from one typed by hand and the
  // suggestion feature is unmeasurable. Always stamped, never defaulted: 0/0 is
  // the truthful value for a flow that shows no learned suggestion (signup).
  total_suggested_topics: number;
  total_suggested_confirmed: number;
}) {
  gaPushEvent(OnboardingEvent.RELEVANT_TOPICS_SUBMITTED, params);
}

export function gaEventPubSearchNoResults() {
  gaPushEvent(OnboardingEvent.PUB_SEARCH_NO_RESULTS);
}

export function gaEventPubSearchError() {
  gaPushEvent(OnboardingEvent.PUB_SEARCH_ERROR);
}
